import { CONFIG } from './config.ts';
import { huntMatchup, matchup, pvpMatchup, type Element } from './elements.ts';
import { MOVE, WAVE_MIN_TARGETS, WAVE_RADIUS, basicMoveOf, cooldownMs, kitAt, type MoveDef } from './moves.ts';
import { FINAL_TOTAL, SPECIES, STAGE_FRACTION } from './species.ts';
import type { Stats } from './stats.ts';
import type { Rng } from './rng.ts';
import { TRAIT_BY_ID } from './traits.ts';
import type { HeldFx } from './held.ts';

export interface Fighter {
  speciesId: string;
  level: number;
  stats: Stats;
  trait: string;
  relicDmg?: { element?: string; mult: number };
  /** Hall Keeper bonus (shared/halls.ts keeperMult): set on the hunt and in boss fights only, never in duels, the arena or Guild War. */
  hallMult?: number;
  /** A Rune Tablet's element (CreatureDTO.tablet): the Coverage slot casts that element's coverage strike. */
  tablet?: string;
  /** PvP synergies (shared/synergy.ts): all damage ×dmg, and a move of element e ×el[e]. Never on the hunt, bosses or Guild War. */
  synergy?: { dmg: number; el: Partial<Record<string, number>> };
  /** A held item's in-fight effects (shared/held.ts): PvP only, set by pvpFighter in the Arena's mode. */
  held?: HeldFx;
}

/**
 * Milliseconds between attacks. Swift is read relative to level (as if level
 * 100), so cadence stays the same as creatures grow: Swift-at-100 of 100 ->
 * ~1.0s, 200 -> ~0.7s.
 */
export function attackInterval(f: Fighter): number {
  const t = TRAIT_BY_ID[f.trait];
  const swift = (f.stats.swift * 105) / (Math.max(1, f.level) + 5);
  return Math.round((CONFIG.attackMs * 120) / (120 + swift) / (t?.speedMult ?? 1));
}

/** mv: the move id (shared/moves.ts). crit: the blow was a critical hit (CRIT). */
export interface Hit { dmg: number; mult: number; crit: boolean; mv: string }

/** Traits, relics and the Hall Keeper bonus: the T term of the damage formula. */
export function bonusOf(att: Fighter): number {
  const a = SPECIES[att.speciesId];
  const t = TRAIT_BY_ID[att.trait];
  let bonus = t?.dmgMult ?? 1;
  if (t?.dmgElement && a.elements.includes(t.dmgElement.element)) bonus *= t.dmgElement.mult;
  if (att.relicDmg && (!att.relicDmg.element || a.elements.includes(att.relicDmg.element as never))) bonus *= att.relicDmg.mult;
  if (att.hallMult) bonus *= att.hallMult;
  return bonus;
}

/**
 * The single 45-power blow on the base chart, with a dual-element attacker's second element at ×0.9: a wild striking
 * your creature on the hunt (times the level edge: wildBlow). Every other fight casts moves (pickMove, strike).
 */
export function damage(att: Fighter, def: Fighter, rng: Rng): Hit {
  const a = SPECIES[att.speciesId], d = SPECIES[def.speciesId];
  const atk = a.style === 'might' ? att.stats.might : att.stats.focus;
  const dfn = a.style === 'might' ? def.stats.guard : def.stats.will;
  const element = a.elements[0];
  let mult = matchup(element, d.elements);
  // Second element of a dual-type attacker hits as a weaker secondary strike if better.
  if (a.elements[1]) mult = Math.max(mult, matchup(a.elements[1], d.elements) * 0.9);
  const bonus = bonusOf(att);
  // Proportional to level with no flat term, so fights take as many blows at level 5 as at level 500.
  const raw = (((2 * att.level) / 5 + 2) * 45 * (atk / Math.max(1, dfn))) / 50;
  const crit = rng.chance(CRIT.pve.chance);
  const dmg = Math.max(1, Math.round(raw * mult * bonus * (crit ? CRIT.pve.mult : 1) * rng.range(ROLL_LO, 1)));
  return { dmg, mult, crit, mv: basicMoveOf(att.speciesId) };
}

// ---------- the move engine: hunts and bosses (battle design, sections 2 and 3) ----------

/** Exponent of the level edge: damage × (attacker level ÷ defender level)^2 on hunts and bosses, both ways. */
export const LEVEL_EDGE_EXP = 2;
/** The level edge (battle design 3.1): ×4 at twice the defender's level, ×0.64 at 80%. Never used in PvP. */
export function levelEdge(attLevel: number, defLevel: number): number {
  return Math.pow(Math.max(1, attLevel) / Math.max(1, defLevel), LEVEL_EDGE_EXP);
}
/** Same-element bonus: a move of one of the attacker's own elements. */
export const SAME_ELEMENT = 1.25;
/**
 * Damage rolls between this share and the full amount, uniform, as in the reference game (0.85-1.00, no misses), on
 * top of the critical hit (CRIT). Every roll comes from the fight's seeded Rng (sim/consistency.ts for win rates).
 */
export const ROLL_LO = 0.85;
/**
 * Critical hits, drawn from the fight's seeded Rng just before the roll, so a seed still replays exactly (retune
 * 2026-10-15, battle-consistency.md). Hunts and bosses: 6% at ×1.5, as before the consistency pass. PvP (Arena,
 * Halls, duels, tournaments) and Guild War: 10% at ×2, so close fights can turn while a clear edge still wins.
 */
export const CRIT = {
  pve: { chance: 0.06, mult: 1.5 },
  pvp: { chance: 0.1, mult: 2 },
} as const;
export const critOf = (chart: Chart) => CRIT[chart === 'pvp' || chart === 'war' ? 'pvp' : 'pve'];
/** A creature acts once per beat: its attack interval, held between these. */
export const BEAT_MIN_MS = 400;
export const BEAT_MAX_MS = 1200;

/**
 * Which ruleset a blow uses: the hunt chart (your blows on a wild), the base chart (wilds on you, bosses), PvP (the
 * PvP chart, ×1.1 / ×0.9, with no level edge: Arena, Halls, duels and tournaments, whose levels are compressed
 * instead), or war (Guild War: the base chart with no level edge, since its Battle Plan target rules are about
 * match-ups). PvP and war blows crit at CRIT.pvp.
 */
export type Chart = 'hunt' | 'base' | 'pvp' | 'war';

export function beatMs(f: Fighter): number {
  return Math.min(BEAT_MAX_MS, Math.max(BEAT_MIN_MS, attackInterval(f)));
}

/** Speed shortens cooldowns by up to 10%: Swift-at-100 from 50 to 200. */
export function cooldownMult(f: Fighter): number {
  const swift = (f.stats.swift * 105) / (Math.max(1, f.level) + 5);
  return 1 - 0.1 * Math.min(1, Math.max(0, (swift - 50) / 150));
}

/** A move's cooldown for this fighter, Speed discount applied. */
export function moveCooldownMs(f: Fighter, m: MoveDef): number {
  return Math.round(cooldownMs(m) * cooldownMult(f));
}

const kitCache = new Map<string, MoveDef[]>();
/**
 * The single-target moves a fighter can cast: its kit at this level (shared/moves.ts kitAt) with its Rune Tablet,
 * the basic attack and the Wave left out (the Wave is the hunt's: waveMove).
 */
export function kitMoves(f: Pick<Fighter, 'speciesId' | 'level' | 'tablet'>): MoveDef[] {
  const key = `${f.speciesId}:${f.level >= 40 ? 40 : f.level >= 20 ? 20 : 1}:${f.tablet ?? ''}`;
  let k = kitCache.get(key);
  if (!k) {
    const kit = kitAt(f.speciesId, f.level, f.tablet);
    k = (['light', 'heavy', 'coverage', 'signature', 'awakened'] as const).filter((s) => kit[s]).map((s) => MOVE[kit[s]!]);
    kitCache.set(key, k);
  }
  return k;
}

/** The fighter's Wave once learned (Lv 60), else null. Cast on the hunt only. */
export function waveMove(f: Pick<Fighter, 'speciesId' | 'level'>): MoveDef | null {
  const id = kitAt(f.speciesId, f.level).wave;
  return id ? MOVE[id] : null;
}

/**
 * The wilds a Wave on `target` also hits: every other one within `radius` tiles of it (centre to centre), nearest
 * first, ties by uid, so the order never depends on the list's order.
 */
export function splashTargets<T extends { uid: number; x: number; y: number }>(target: T, others: readonly T[], radius = WAVE_RADIUS): T[] {
  const d = (w: T) => Math.hypot(w.x - target.x, w.y - target.y);
  return others.filter((w) => w.uid !== target.uid && d(w) <= radius).sort((a, b) => d(a) - d(b) || a.uid - b.uid);
}

/**
 * The hunt's auto-cast (battle design 2.1, rule 4 first): the Wave when it is ready and `inReach` wilds (the target
 * included) are within its radius, at least WAVE_MIN_TARGETS; otherwise pickMove on the target.
 */
export function pickHuntMove(att: Fighter, def: Fighter, ready: (m: MoveDef) => boolean, defHp: number, inReach: number): MoveDef {
  const wave = waveMove(att);
  if (wave && inReach >= WAVE_MIN_TARGETS && ready(wave)) return wave;
  return pickMove(att, def, 'hunt', ready, defHp);
}

export function basicMove(f: Pick<Fighter, 'speciesId'>): MoveDef {
  return MOVE[basicMoveOf(f.speciesId)];
}

/** Element multiplier of a move on a defender, on the given chart. */
export function chartMult(m: MoveDef, def: Fighter, chart: Chart): number {
  const els = SPECIES[def.speciesId].elements;
  return chart === 'hunt' ? huntMatchup(m.element, els) : chart === 'pvp' ? pvpMatchup(m.element, els) : matchup(m.element, els);
}

/** The auto-cast score of a move: power × same-element × chart. */
export function moveScore(att: Fighter, def: Fighter, m: MoveDef, chart: Chart): number {
  return m.power * (SPECIES[att.speciesId].elements.includes(m.element as Element) ? SAME_ELEMENT : 1) * chartMult(m, def, chart);
}

/**
 * Formula 3.1 before the roll:
 * ((2L/5 + 2) × P × A/D) / 50 × same-element × chart × traits × level edge × synergies (PvP only).
 * `mult` is the chart value alone (what the client tints a number by).
 */
export function blowBase(att: Fighter, def: Fighter, m: MoveDef, chart: Chart): { raw: number; mult: number } {
  const a = SPECIES[att.speciesId];
  const atk = a.style === 'might' ? att.stats.might : att.stats.focus;
  const dfn = a.style === 'might' ? def.stats.guard : def.stats.will;
  const mult = chartMult(m, def, chart);
  const same = a.elements.includes(m.element as Element) ? SAME_ELEMENT : 1;
  const edge = chart === 'pvp' || chart === 'war' ? 1 : levelEdge(att.level, def.level);
  const syn = att.synergy ? att.synergy.dmg * (att.synergy.el[m.element] ?? 1) : 1;
  const raw = ((((2 * att.level) / 5 + 2) * m.power * atk) / Math.max(1, dfn) / 50) * same * mult * bonusOf(att) * edge * syn;
  // A Spirit Bead (shared/held.ts): moves of its element hit harder.
  if (att.held?.el === m.element) return { raw: raw * (att.held.elMult ?? 1), mult };
  return { raw, mult };
}

/**
 * The auto-cast rule (battle design 2.1): the ready move with the best power × same-element × chart, only if it
 * beats the basic attack's score, and never when the basic attack's lowest roll already finishes the defender.
 */
export function pickMove(att: Fighter, def: Fighter, chart: Chart, ready: (m: MoveDef) => boolean, defHp: number): MoveDef {
  const basic = basicMove(att);
  if (blowBase(att, def, basic, chart).raw * ROLL_LO >= defHp) return basic;
  let best = basic, bv = moveScore(att, def, basic, chart);
  for (const m of kitMoves(att)) {
    if (!ready(m)) continue;
    const v = moveScore(att, def, m, chart);
    if (v > bv) { best = m; bv = v; }
  }
  return best;
}

/**
 * One blow of a move: formula 3.1 with a critical hit (CRIT, by chart) and a roll of 0.85-1.00. `share` scales the power (a Wave's splash is the
 * formula at power × WAVE_SPLASH).
 */
export function strike(att: Fighter, def: Fighter, m: MoveDef, chart: Chart, rng: Rng, share = 1): Hit {
  const b = blowBase(att, def, m, chart);
  const raw = b.raw * share, mult = b.mult;
  const c = critOf(chart);
  const crit = rng.chance(c.chance + (att.held?.crit ?? 0));
  const dmg = Math.max(1, Math.round(raw * (crit ? c.mult : 1) * rng.range(ROLL_LO, 1)));
  return { dmg, mult, crit, mv: m.id };
}

/** A wild's blow on your creature: its basic hit on the base chart (`damage`), times its level edge on you. */
export function wildBlow(wild: Fighter, lead: Fighter, rng: Rng): Hit {
  const h = damage(wild, lead, rng);
  return { ...h, dmg: Math.max(1, Math.round(h.dmg * levelEdge(wild.level, lead.level))) };
}

/**
 * What one blow of a move does to a defender with `hp` left, for the calculator and the Bestiary: damage at the
 * lowest and highest roll (no crit), hits to finish at those rolls, and the chance one blow finishes it (rolls and
 * crits counted). It is the formula the hunt uses, so 100% here is a one-shot in the field and 0% never is.
 */
export interface BlowOdds { move: MoveDef; mult: number; lo: number; hi: number; hitsLo: number; hitsHi: number; oneHit: number }
export function blowOdds(att: Fighter, def: Fighter, m: MoveDef, chart: Chart, hp: number, splash = 1): BlowOdds {
  const b = blowBase(att, def, m, chart);
  const raw = b.raw * splash, mult = b.mult;
  const lo = Math.max(1, Math.round(raw * ROLL_LO)), hi = Math.max(1, Math.round(raw));
  // Share of rolls r in [ROLL_LO, 1] with round(raw × c × r) ≥ hp.
  const share = (c: number) => { const need = (hp - 0.5) / Math.max(1e-9, raw * c); return Math.min(1, Math.max(0, (1 - need) / (1 - ROLL_LO))); };
  const cr = critOf(chart);
  const oneHit = (1 - cr.chance) * share(1) + cr.chance * share(cr.mult);
  return { move: m, mult, lo, hi, hitsLo: Math.ceil(hp / hi), hitsHi: Math.ceil(hp / lo), oneHit };
}

/**
 * How much tougher or frailer than a typical creature of its stage a species
 * is (vigor × defences at level 100), inverted, times the square root of its
 * tier's stat total over the reference total. Wild vigor is scaled by this: a
 * species' shape does not decide how many blows it takes, but its tier does,
 * so a rarer species takes more blows (an Epic about 1.18× a Common's) and
 * pays less XP an hour than a Common farm at the same level. Element matchups and the lead's
 * own attack still make the rest of the difference.
 */
const bulkCache = new Map<string, number>();
export function wildBulkFactor(speciesId: string): number {
  let f = bulkCache.get(speciesId);
  if (f !== undefined) return f;
  const sp = SPECIES[speciesId];
  const bulk = (b: readonly number[]) => (2 * b[0] + 125) * 0.5 * (1 / (2 * b[2] + 20) + 1 / (2 * b[4] + 20));
  const even = (REFERENCE_TOTAL * STAGE_FRACTION[sp.stage - 1]) / 6;
  f = (bulk([even, even, even, even, even, even]) / bulk(sp.base)) * Math.sqrt(FINAL_TOTAL[sp.rarity] / REFERENCE_TOTAL);
  bulkCache.set(speciesId, f);
  return f;
}
const REFERENCE_TOTAL = 470;

/** A wild's stats: vigor scaled by CONFIG.wildHpMult and the species' bulk factor, attack by CONFIG.wildAtkMult. */
export function wildStats(speciesId: string, st: Stats): Stats {
  return {
    ...st,
    hp: Math.max(1, Math.round(st.hp * CONFIG.wildHpMult * wildBulkFactor(speciesId))),
    might: Math.round(st.might * CONFIG.wildAtkMult),
    focus: Math.round(st.focus * CONFIG.wildAtkMult),
  };
}
